import { formatInTimeZone } from "date-fns-tz";
import enGB from "date-fns/locale/en-GB";
import { parseISO, format, parse } from "date-fns";

function convertStringDateToDateWithFormat(date: Date): string {
  let nextNonWorkingDayDisplayValue: string = "";

  try {
    nextNonWorkingDayDisplayValue = formatInTimeZone(
      date,
      "Europe/London",
      "d MMM yyyy",
      { locale: enGB }
    );
  } catch (e) {
    nextNonWorkingDayDisplayValue = "loading date";
  }

  return nextNonWorkingDayDisplayValue;
}

function convertStringToDate(date: string): Date | undefined {
  let convertedDate: Date;

  try {
    convertedDate = parseISO(date);
  } catch (e) {
    return undefined;
  }

  return convertedDate;
}

function areEqual(
  first: Date | undefined,
  second: string | undefined
): boolean {
  try {
    if (first === undefined) return false;
    if (second === undefined) return false;

    const secondAsDate: Date | undefined = convertStringToDate(second!);

    if (secondAsDate) {
      return (
        format(first!, "yyyy-MM-dd") !== format(secondAsDate!, "yyyy-MM-dd")
      );
    }

    return false;
  } catch (exception) {
    return false;
  }
}

function convertToDateArray(
  first: Date | undefined,
  second: Date | undefined
): Date[] | undefined {
  if (first === undefined) return undefined;
  if (second === undefined) return undefined;

  return [first, second];
}

function formatLocalDateTime(
  date: Date,
  dateTimeFormat: string
): string | undefined {
  let formattedDate: string | undefined;

  try {
    if (date !== undefined) {
      formattedDate = formatInTimeZone(date!, "Europe/London", dateTimeFormat, {
        locale: enGB
      });
    }
  } catch (e) {
    formattedDate = undefined;
  }

  return formattedDate;
}

function formatDateWithTime(date?: Date): string | undefined {
  let formattedDate: string | undefined;

  try {
    if (date !== undefined) {
      formattedDate = formatLocalDateTime(date, "dd MMM yyyy HH:mm");
    }
  } catch (e) {
    formattedDate = undefined;
  }

  return formattedDate;
}

function format2DateWithTime(date?: Date): string | undefined {
  let formattedDate: string | undefined;

  try {
    if (date !== undefined) {
      formattedDate = formatLocalDateTime(date, "dd/MM/yyyy HH:mm");
    }
  } catch (e) {
    formattedDate = undefined;
  }

  return formattedDate;
}

function formatDecimalToHoursAndMinutes(value: Number): string | undefined {
  let formattedDate: string | undefined;

  try {
    if (value !== undefined) {
      const n = new Date(0, 0);
      n.setSeconds((value as number) * 60 * 60);
      formattedDate = `${n.getHours().toLocaleString()} Hours`;

      const minutes = n.getMinutes();

      if (minutes !== 0) {
        formattedDate += ` ${minutes} Minutes`;
      }
    }
  } catch (e) {
    formattedDate = undefined;
  }

  return formattedDate;
}

function convertHoursAndMinutesToDecimal(
  hours: number,
  minutes: number
): number | undefined {
  let asDecimal: number | undefined;

  try {
    asDecimal = (hours * 60 + minutes) / 60;
  } catch (e) {
    asDecimal = undefined;
  }

  return asDecimal;
}

const convertUtcDateToLocalDate = (date: Date) => {
  const newDate = new Date(
    date.getTime() + date.getTimezoneOffset() * 60 * 1000
  );

  const offset = date.getTimezoneOffset() / 60;
  const hours = date.getHours();

  newDate.setHours(hours - offset);

  return newDate;
};

const convertToLocalDate = (date: Date): string => formatInTimeZone(date!, "Europe/London", "yyyyMMdd", { locale: enGB });
const convertToDateFromLocalDate = (localDate: string): Date => parse(localDate, "yyyyMMdd", new Date());

const sameDay = (d1: Date, d2: Date): boolean => d1.getUTCFullYear() === d2.getUTCFullYear()
  && d1.getUTCMonth() === d2.getUTCMonth()
  && d1.getUTCDate() === d2.getUTCDate();

export {
  convertStringDateToDateWithFormat,
  convertStringToDate,
  areEqual,
  convertToDateArray,
  formatDateWithTime,
  formatDecimalToHoursAndMinutes,
  convertHoursAndMinutesToDecimal,
  convertUtcDateToLocalDate,
  convertToLocalDate,
  convertToDateFromLocalDate,
  sameDay,
  format2DateWithTime,
  formatLocalDateTime
};
